import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setDone(true);
      toast.success('Contraseña actualizada con éxito');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-orange-400 mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Token inválido</h2>
            <p className="text-sm text-zinc-400">
              El link no es válido o ya expiró. Pedí uno nuevo.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full">Pedir otro link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-card border-border">
          <CardContent className="p-8">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Nueva contraseña</h1>
              <p className="text-sm text-zinc-400">
                Elige una contraseña nueva para tu cuenta.
              </p>
            </div>

            {done ? (
              <div className="space-y-4 text-center" data-testid="reset-success-block">
                <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto" />
                <h2 className="text-xl font-semibold text-foreground">¡Listo!</h2>
                <p className="text-sm text-zinc-400">
                  Tu contraseña fue actualizada. Te llevamos al login...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nueva contraseña</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                    className="bg-zinc-800/50 border-zinc-700 focus:border-primary h-12"
                    data-testid="reset-new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Repetí la contraseña</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repetí tu contraseña"
                    required
                    minLength={6}
                    className="bg-zinc-800/50 border-zinc-700 focus:border-primary h-12"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-primary font-bold uppercase tracking-wide"
                  disabled={loading}
                  data-testid="reset-submit-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Cambiar contraseña
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
